import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

interface LanguageSelectorProps {
  scrolled?: boolean;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ scrolled = false }) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const languages = [
    { code: 'fr', label: 'FR', flag: '🇫🇷' },
    { code: 'en', label: 'EN', flag: '🇬🇧' },
    { code: 'zh', label: '中文', flag: '🇨🇳' }
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];
  const otherLanguages = languages.filter(lang => lang.code !== i18n.language);

  const handleLanguageChange = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className={`px-3 py-2 text-sm font-body tracking-wider transition-all duration-300 flex items-center gap-2 ${
          scrolled
            ? 'bg-white text-koi-dark border border-koi-red/30 hover:border-koi-red hover:bg-koi-red/10'
            : 'bg-gray-800 text-white border border-gray-700 hover:bg-gray-700'
        }`}
      >
        <span>{currentLanguage.flag}</span>
        {currentLanguage.label}
        <ChevronDown 
          size={14} 
          className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-full left-0 mt-1 bg-white border border-koi-red/30 shadow-lg z-50 min-w-full"
          >
            {otherLanguages.map((lang) => (
              <motion.button
                key={lang.code}
                whileHover={{ backgroundColor: 'rgba(255, 102, 102, 0.1)' }}
                onClick={() => handleLanguageChange(lang.code)}
                className="w-full px-3 py-2 text-sm font-body tracking-wider text-koi-dark hover:text-koi-red transition-colors duration-200 flex items-center gap-2 text-left"
              >
                <span>{lang.flag}</span>
                {lang.label}
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LanguageSelector;